import _find from 'lodash/find';
import _remove from 'lodash/remove';
import _without from 'lodash/without';

import {
  fetchComments,
  updateCommentBuckets,
  updateCommentReplyCount,
  updateComments,
  updateFetchingCommentsError,
  updateIsFetching,
} from 'actions/comments';
import { showErrorBar } from 'actions/errorBar';
import { ERROR } from 'constants/error-severities';
import { HEADER_CONTENT_TYPE } from 'constants/http';
import { JSON as JSON_MIME } from 'constants/mimes';
import { isReply } from 'utilities/comments';
import * as loggerx from 'utilities/loggerx';

import { getBuckets } from '../utilities/commentBuckets';

const SERVER_ERROR = 'serverError';

export function deleteCommentCreator(id) {
  return (dispatch, getState) => {
    dispatch(updateIsFetching(true));

    const { password } = getState().share_video;
    const { comments } = getState().comments;

    return fetch(`/v1/comments/post/${id}`, {
      credentials: 'include',
      headers: { [HEADER_CONTENT_TYPE]: JSON_MIME },
      method: 'DELETE',
      body: JSON.stringify({ password }),
    })
      .then(resp => {
        if (resp.status >= 400) {
          throw {
            status: resp.status,
            type: SERVER_ERROR,
            message: 'Server error deleting comment',
          };
        }

        return;
      })
      .then(() => {
        const newCommentArray = comments.slice(0);
        const commentToRemove = _find(newCommentArray, { id });

        commentToRemove.locallyDeleted = true;

        dispatch(updateCommentsAndBuckets(newCommentArray));
        dispatch(updateIsFetching(false));
      })
      .catch(err => {
        dispatch(showErrorBar(ERROR, err.message));
        dispatch(updateIsFetching(false));

        if (err.type === SERVER_ERROR) {
          return;
        }

        loggerx.error(err, { commentId: id }, { team: 'create' });
      });
  };
}

export function restoreCreator(commentOrReply) {
  return (dispatch, getState) => {
    dispatch(updateIsFetching(true));

    const { password } = getState().share_video;
    const { comments } = getState().comments;

    const restorePath = isReply(commentOrReply) ? 'reply' : 'post';

    return fetch(`/v1/comments/${restorePath}/${commentOrReply.id}/restore`, {
      credentials: 'include',
      headers: { [HEADER_CONTENT_TYPE]: JSON_MIME },
      method: 'PUT',
      body: JSON.stringify({ password }),
    })
      .then(resp => {
        if (resp.status >= 400) {
          throw {
            status: resp.status,
            type: SERVER_ERROR,
            message: 'Server error deleting reply',
          };
        }

        return resp.json();
      })
      .then(commentOrReplyJSON => {
        const newCommentArray = comments.slice(0);

        let objectToRestore;

        if (isReply(commentOrReplyJSON)) {
          const parentComment = {
            ..._find(newCommentArray, {
              id: commentOrReplyJSON.comment_post_id,
            }),
          };

          objectToRestore = _find(parentComment.replies, {
            id: commentOrReplyJSON.id,
          });
        } else {
          objectToRestore = _find(newCommentArray, {
            id: commentOrReplyJSON.id,
          });
        }

        objectToRestore.locallyDeleted = false;
        Object.assign(objectToRestore, commentOrReplyJSON);

        dispatch(updateCommentsAndBuckets(newCommentArray));
        dispatch(updateIsFetching(false));
      });
  };
}

export function removeLocalDeleted(commentOrReply) {
  return (dispatch, getState) => {
    const { comments } = getState().comments;
    const newCommentArray = comments.slice(0);

    if (isReply(commentOrReply)) {
      const reply = commentOrReply;

      const parentComment = _find(newCommentArray, {
        id: reply.comment_post_id,
      });

      parentComment.replies = _without(parentComment.replies, reply);
    } else {
      _remove(newCommentArray, {
        id: commentOrReply.id,
      });
    }

    dispatch(updateCommentsAndBuckets(newCommentArray));
  };
}

export function removeAllLocalDeleted() {
  return (dispatch, getState) => {
    const { comments } = getState().comments;

    if (!comments.length) {
      return;
    }

    const newCommentArray = comments.slice(0);

    // remove locally deleted posts
    _remove(newCommentArray, post => Boolean(post.locallyDeleted));

    // remove locally deleted replies
    newCommentArray.forEach(post => {
      _remove(post.replies, reply => Boolean(reply.locallyDeleted));
    });

    dispatch(updateCommentsAndBuckets(newCommentArray));
  };
}

export function deleteReplyCreator(reply) {
  return (dispatch, getState) => {
    const { password } = getState().share_video;
    const { comments } = getState().comments;

    return fetch(`/v1/comments/reply/${reply.id}`, {
      credentials: 'include',
      headers: { [HEADER_CONTENT_TYPE]: JSON_MIME },
      method: 'DELETE',
      body: JSON.stringify({ password }),
    })
      .then(resp => {
        if (resp.status >= 400) {
          throw {
            status: resp.status,
            type: SERVER_ERROR,
            message: 'Server error deleting reply',
          };
        }

        return;
      })
      .then(() => {
        const newCommentsArr = comments.slice(0);
        const parentComment = _find(newCommentsArr, {
          id: reply.comment_post_id,
        });

        const replyToDelete = _find(parentComment.replies, { id: reply.id });

        replyToDelete.locallyDeleted = true;

        dispatch(updateCommentsAndBuckets(newCommentsArr));
      })
      .catch(err => {
        dispatch(showErrorBar(ERROR, err.message));

        if (err.type === SERVER_ERROR) {
          return;
        }

        loggerx.error(
          err,
          { commentId: reply.comment_post_id, replyId: reply.id },
          { team: 'create' }
        );
      });
  };
}

export function fetchCommentsCreator() {
  return (dispatch, getState) => {
    const {
      video: { id, comments_enabled: commentsEnabled },
      password,
    } = getState().video_player;

    if (!commentsEnabled) {
      return;
    }

    dispatch(fetchComments());

    const payload = {
      video_id: id,
      password,
    };

    return fetch('/v1/comments', {
      credentials: 'include',
      headers: { [HEADER_CONTENT_TYPE]: JSON_MIME },
      method: 'POST',
      body: JSON.stringify(payload),
    })
      .then(resp => {
        if (resp.status >= 400) {
          throw {
            status: resp.status,
            type: SERVER_ERROR,
            message: 'Server error fetching comments',
          };
        }

        return resp.json();
      })
      .then(commentsJSON => {
        dispatch(updateCommentsAndBuckets(commentsJSON));
      })
      .catch(err => {
        dispatch(updateFetchingCommentsError(err.message));
        dispatch(showErrorBar(ERROR, err.message));

        if (err.type === SERVER_ERROR) {
          return;
        }

        loggerx.error(err, { videoId: payload.video_id }, { team: 'create' });
      });
  };
}

export function updateCommentsAndBuckets(newComments) {
  return (dispatch, getState) => {
    const videoLength = getState().video_player.duration;

    dispatch(updateComments(newComments));

    const buckets = getBuckets(newComments, videoLength);

    dispatch(updateCommentBuckets(buckets));

    const commentReplyCount = Object.values(buckets).reduce(
      (sum, bucket) => sum + bucket.commentCount,
      0
    );

    dispatch(updateCommentReplyCount(commentReplyCount));
  };
}

export function updateBuckets(comments) {
  return (dispatch, getState) => {
    const videoLength = getState().video_player.duration;

    const buckets = getBuckets(comments, videoLength);

    dispatch(updateCommentBuckets(buckets));

    const commentReplyCount = Object.values(buckets).reduce(
      (sum, bucket) => sum + bucket.commentCount,
      0
    );

    dispatch(updateCommentReplyCount(commentReplyCount));
  };
}
